import { INSTAGRAM_IMAGE_MAX_BYTES } from 'fym-shared/src/music24/postImageRules';
import { POST_VIDEO_UPLOAD_MAX_BYTES, POST_VIDEO_UPLOAD_TYPES } from 'fym-shared/src/music24/postVideoRules';
import { PostVideoMeasures, SerializedPostVideo } from '../../../../../redux/api/fymAPI.types';
import { PromoteMediaDraft } from '../../draftStore';
import { inlineMediaUrl } from '../../inlineMediaUrl';
import { getFullAPIFileURL } from '../../../../common/getFullAPIFileURL';
import { pickerAspectLabel } from './pickerFiles';

/**
 * The upload of the Composer media picker: an image or a video from the user's computer.
 *
 * An image: the browser makes a JPEG of the picked image before it sends it
 * (`prepareUploadImage`): Instagram takes JPEG only, at most 8 MB, so a PNG or a WebP would fail at
 * the hour of the post. The JPEG keeps the size of the image, or has a long side of
 * `UPLOAD_MAX_LONG_SIDE_PX`. `POST /accounts/:accountId/files` (music24-api `singleFileUpload`)
 * takes at most 25 MB (`MAX_UPLOAD_BYTES` in `music24-api/src/app/middlewares/multer.ts`). The
 * picked file may be that large; the JPEG that goes up is at most `INSTAGRAM_IMAGE_MAX_BYTES`.
 *
 * A video (MP4 or MOV, at most 1 GB): it goes up as it is, straight to S3 in parts
 * (`usePickerVideoUpload`, music24-api `promo/post-media/uploads`). The video rules of each
 * platform (fym-shared `postVideoRules.ts`) then say which channel can take it.
 */
export const PICKER_UPLOAD_MAX_BYTES = 25 * 1024 * 1024;

/** The largest video: the largest that a platform takes (Facebook and Threads: 1 GB). */
export const PICKER_VIDEO_MAX_BYTES = POST_VIDEO_UPLOAD_MAX_BYTES;

export const PICKER_UPLOAD_TYPES: ReadonlyArray<string> = ['image/jpeg', 'image/png', 'image/webp'];

export const PICKER_VIDEO_TYPES: ReadonlyArray<string> = POST_VIDEO_UPLOAD_TYPES;

/** The `accept` value of the file input. The extensions help a system that has no type for a MOV. */
export const PICKER_UPLOAD_ACCEPT = [...PICKER_UPLOAD_TYPES, ...PICKER_VIDEO_TYPES, '.mp4', '.mov'].join(',');

export type PickerUploadKind = 'image' | 'video';

/** The long side of the JPEG that goes up, at most. */
export const UPLOAD_MAX_LONG_SIDE_PX = 4096;

/** The JPEG qualities to try, best first, until the file is small enough. */
export const UPLOAD_JPEG_QUALITIES: ReadonlyArray<number> = [0.9, 0.82, 0.74];

export type PickerUploadRefusal = 'type' | 'size';

/** The image type of a file name extension, or null. */
const TYPE_OF_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  jfif: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
};

/**
 * The type of a picked file: the browser's type, else the type of its extension. Some browsers and
 * systems give no type (`''`) for a file they do not know (a MOV on some systems). Null when
 * neither tells: the image decode then decides.
 */
export function pickerUploadType(file: Pick<File, 'type' | 'name'>): string | null {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (file.type !== '' && file.type !== 'application/octet-stream') {
    return file.type;
  }
  return TYPE_OF_EXTENSION[extension] ?? null;
}

/** An image goes the image way, a video the video way. A file of no known type is tried as an image. */
export function pickerUploadKind(file: Pick<File, 'type' | 'name'>): PickerUploadKind {
  const type = pickerUploadType(file);
  return type !== null && PICKER_VIDEO_TYPES.includes(type) ? 'video' : 'image';
}

/** Why the picker refuses a file before it reads it, or null when it can go on. */
export function pickerUploadRefusal(file: Pick<File, 'type' | 'size' | 'name'>): PickerUploadRefusal | null {
  const type = pickerUploadType(file);
  if (type !== null && !PICKER_UPLOAD_TYPES.includes(type) && !PICKER_VIDEO_TYPES.includes(type)) {
    return 'type';
  }
  const maxBytes = pickerUploadKind(file) === 'video' ? PICKER_VIDEO_MAX_BYTES : PICKER_UPLOAD_MAX_BYTES;
  return file.size > maxBytes ? 'size' : null;
}

/** How long the picker waits for the browser to read the metadata of a video. */
const VIDEO_MEASURE_TIMEOUT_MS = 10_000;

/**
 * The width, the height and the length of a video, as the browser reads them from its metadata.
 * Null when the browser cannot read the file (a codec it does not know) or gives no answer in 10 s.
 * The server measures the stored file with ffprobe and uses these values only when it cannot.
 */
export function measureVideoFile(file: Blob): Promise<PostVideoMeasures | null> {
  if (typeof URL.createObjectURL !== 'function' || typeof document === 'undefined') {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    const done = (result: PostVideoMeasures | null) => {
      window.clearTimeout(timeout);
      video.removeAttribute('src');
      URL.revokeObjectURL(url);
      resolve(result);
    };
    const timeout = window.setTimeout(() => done(null), VIDEO_MEASURE_TIMEOUT_MS);
    video.preload = 'metadata';
    video.muted = true;
    video.onloadedmetadata = () => {
      const { videoWidth, videoHeight, duration } = video;
      const valid = videoWidth > 0 && videoHeight > 0 && Number.isFinite(duration) && duration > 0;
      done(valid ? { width: videoWidth, height: videoHeight, durationSeconds: duration } : null);
    };
    video.onerror = () => done(null);
    video.src = url;
  });
}

export interface ImageSize {
  width: number;
  height: number;
}

/** The size of the JPEG: the size of the image, or a long side of `maxLongSide`. */
export function uploadTargetSize(size: ImageSize, maxLongSide: number = UPLOAD_MAX_LONG_SIDE_PX): ImageSize {
  const longSide = Math.max(size.width, size.height);
  if (longSide <= maxLongSide) {
    return size;
  }
  const scale = maxLongSide / longSide;
  return { width: Math.max(1, Math.round(size.width * scale)), height: Math.max(1, Math.round(size.height * scale)) };
}

/** `poster.png` → `poster.jpg`. */
export function uploadJpegName(name: string): string {
  const base = name.replace(/\.[^./\\]*$/, '');
  return `${base === '' ? 'image' : base}.jpg`;
}

/** How long the picker waits for the browser to read an image. */
const DECODE_TIMEOUT_MS = 10_000;

/** Reads an image file in the browser. Null when the browser cannot read it (not an image, broken, timeout). */
export function decodeImageFile(file: Blob): Promise<HTMLImageElement | null> {
  if (typeof URL.createObjectURL !== 'function' || typeof Image === 'undefined') {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    const done = (result: HTMLImageElement | null) => {
      window.clearTimeout(timeout);
      URL.revokeObjectURL(url);
      resolve(result);
    };
    const timeout = window.setTimeout(() => done(null), DECODE_TIMEOUT_MS);
    image.onload = () => done(image.naturalWidth > 0 && image.naturalHeight > 0 ? image : null);
    image.onerror = () => done(null);
    image.src = url;
  });
}

function canvasJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
  });
}

export type PreparedUpload =
  | { status: 'ready'; blob: Blob; fileName: string; size: ImageSize }
  | { status: 'refused'; reason: PickerUploadRefusal };

/**
 * The JPEG that goes up: the image drawn on a white ground (a PNG can be transparent; a JPEG has
 * no alpha), at its own size or a long side of 4096 px, at quality 0.9, then lower, then smaller,
 * until it is at most 8 MB. A file the browser cannot read is refused as `type`; an image that
 * stays above 8 MB is refused as `size`.
 */
export async function prepareUploadImage(file: File): Promise<PreparedUpload> {
  const image = await decodeImageFile(file);
  if (image === null) {
    return { status: 'refused', reason: 'type' };
  }
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (context === null) {
    return { status: 'refused', reason: 'type' };
  }
  const natural = { width: image.naturalWidth, height: image.naturalHeight };
  // The long side shrinks by a quarter each round when the lowest quality is still too large.
  for (let maxLongSide = UPLOAD_MAX_LONG_SIDE_PX; maxLongSide >= 1024; maxLongSide = Math.round(maxLongSide * 0.75)) {
    const size = uploadTargetSize(natural, maxLongSide);
    canvas.width = size.width;
    canvas.height = size.height;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, size.width, size.height);
    context.drawImage(image, 0, 0, size.width, size.height);
    for (const quality of UPLOAD_JPEG_QUALITIES) {
      // eslint-disable-next-line no-await-in-loop -- each try depends on the size of the one before
      const blob = await canvasJpeg(canvas, quality);
      if (blob === null) {
        return { status: 'refused', reason: 'type' };
      }
      if (blob.size <= INSTAGRAM_IMAGE_MAX_BYTES) {
        return { status: 'ready', blob, fileName: uploadJpegName(file.name), size };
      }
    }
  }
  return { status: 'refused', reason: 'size' };
}

/**
 * The Composer media of an uploaded image. An upload belongs to no song: it carries no
 * `catalogItemUuid`, so it never changes the release (and the SmartLink) of the post.
 */
export function mediaDraftOfUpload(input: {
  fileId: number;
  url: string;
  name: string;
  size: ImageSize | null;
}): PromoteMediaDraft {
  const width = input.size?.width ?? null;
  const height = input.size?.height ?? null;
  return {
    fileId: input.fileId,
    fileUrl: getFullAPIFileURL(input.url),
    thumbnailUrl: inlineMediaUrl(input.url),
    kind: 'image',
    aspect: pickerAspectLabel({ width, height }),
    width,
    height,
    durationSec: null,
    label: input.name,
    title: '',
    artistName: '',
    renderedAt: new Date().toISOString(),
  };
}

/**
 * The Composer media of an uploaded video: the saved file with its size and its measures (the
 * server's, which are ffprobe's or the browser's). Like an image upload it belongs to no song.
 * It has no picture: the preview plays the video itself.
 */
export function mediaDraftOfVideoUpload(input: {
  file: SerializedPostVideo;
  name: string;
  /** The browser measures, for a value that the server could not give. */
  measures: PostVideoMeasures | null;
}): PromoteMediaDraft {
  const width = input.file.width ?? input.measures?.width ?? null;
  const height = input.file.height ?? input.measures?.height ?? null;
  return {
    fileId: input.file.id,
    fileUrl: getFullAPIFileURL(input.file.url),
    kind: 'video',
    aspect: pickerAspectLabel({ width, height }),
    width,
    height,
    durationSec: input.file.durationSeconds ?? input.measures?.durationSeconds ?? null,
    sizeBytes: input.file.sizeBytes,
    label: input.name,
    title: '',
    artistName: '',
    renderedAt: new Date().toISOString(),
  };
}

/** Why an upload failed, for the line under the upload box. */
export type PickerUploadFailure = 'limit' | 'unreadable' | 'other';

/**
 * The reason of a failed upload, from the API error (`customBaseQuery`: `statusCode`, and the REST
 * `cause` on `m24Limit`): 429 is a limit of the video uploads, `PostVideoUnreadable` a video that
 * the server cannot read. Every other error is `other`.
 */
export function pickerUploadFailure(error: unknown): PickerUploadFailure {
  if (typeof error !== 'object' || error === null) {
    return 'other';
  }
  const { statusCode, m24Limit } = error as { statusCode?: unknown; m24Limit?: { code?: unknown } | null };
  if (statusCode === 429) {
    return 'limit';
  }
  return m24Limit?.code === 'PostVideoUnreadable' ? 'unreadable' : 'other';
}
